import React, {useState, useRef, useEffect} from "react";
import "./action-card.css";
import Tooltip from "../tooltip/tooltip";

const getContrastColor = (hex) => {
    const c = hex.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 186 ? "#394F65" : "#FFF";
};

const ActionCard = ({
    title,
    dropdownItems = [],
    onClick,
    active = false,
    background = "#394F65",
    textColor,
    footerColor,
    disabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [alignRight, setAlignRight] = useState(false);
    const cardRef = useRef(null);
    const toggleRef = useRef(null);

    const autoTextColor = getContrastColor(background);
    const appliedTextColor = textColor || autoTextColor;
    const appliedFooterColor = footerColor || autoTextColor;

    useEffect(() => {
        if (open && toggleRef.current) {
            const rect = toggleRef.current.getBoundingClientRect();
            const spaceRight = window.innerWidth - rect.right;
            const estimatedWidth = 200;

            setAlignRight(spaceRight < estimatedWidth);
        }
    }, [open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (cardRef.current && !cardRef.current.contains(event.target)) {
                setOpen(false);
            }
        }

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    return (
        <div
            ref={cardRef}
            className={`action-card py-2 ${active ? "active" : ""} ${disabled ? "disabled" : ""}`}
            onClick={!disabled ? onClick : undefined}
            style={
                !disabled
                    ? {
                        background,
                        borderColor: background,
                        color: appliedTextColor,
                    }
                    : undefined
            }
        >
            <div className="action-card-header">
                {dropdownItems.length > 0 && (
                    <div className="dropdown-container" ref={toggleRef}>
                        <div
                            className="dropdown-toggle"
                            onClick={(e) => {
                                if (disabled) return;
                                e.stopPropagation();
                                setOpen(!open);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill={!disabled ? appliedFooterColor : "#FFFFFF"}
                                stroke={!disabled ? appliedFooterColor : "#FFFFFF"}
                            >
                                <circle cx="12" cy="12" r="1" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="12" cy="18" r="1" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="12" cy="6" r="1" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </div>

                        {open && dropdownItems.length > 0 && (
                            <div
                                className={`dropdown-menu px-2 ${open ? "show" : ""} ${
                                    alignRight ? "drop-left" : "drop-right"
                                }`}
                            >
                                {dropdownItems.map((group, groupIndex) => (
                                    <div key={groupIndex}>
                                        {group.group.map((item, index) => {
                                            const content = (
                                                <div
                                                    key={index}
                                                    className="dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        item.onClick?.();
                                                        setOpen(false);
                                                    }}
                                                >
                                                    {item.label}
                                                </div>
                                            );

                                            return item.tooltip ? (
                                                <Tooltip
                                                    key={index}
                                                    variant="rich"
                                                    position="left"
                                                    title={item.tooltip.title}
                                                    description={item.tooltip.description}
                                                >
                                                    {content}
                                                </Tooltip>
                                            ) : (
                                                content
                                            );
                                        })}
                                        {groupIndex < dropdownItems.length - 1 && (
                                            <div className="dropdown-separator m-1" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}


                    </div>
                )}
            </div>

            <div className="gap-2 d-flex flex-column px-2">
                <span
                    className="action-card-title"
                    style={{color: !disabled ? appliedTextColor : "#9DACBD"}}
                >
                    {title}
                </span>
                <div
                    className="action-card-footer"
                    style={{backgroundColor: !disabled ? appliedFooterColor : "#FFFFFF"}}
                />
            </div>
        </div>
    );
};

export default ActionCard;
